'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Sparkles, X, Loader2, TrendingUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/lib/hooks/useDebounce';

interface SearchSuggestion {
  text: string;
  type: 'recent' | 'suggested' | 'trending';
}

interface PublicSearchBarProps {
  showExamples?: boolean;
}

export default function PublicSearchBar({ showExamples = true }: PublicSearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Load suggestions based on query
  useEffect(() => {
    if (debouncedQuery.length > 1) {
      // TODO: Implement actual suggestion API
      // For now, show mock suggestions
      setSuggestions([
        { text: `${debouncedQuery} in Engineering`, type: 'suggested' },
        { text: `${debouncedQuery} tutorials`, type: 'suggested' },
        { text: `${debouncedQuery} best practices`, type: 'suggested' },
      ]);
    } else if (isFocused && !query) {
      // Show trending/recent when focused but no query
      setSuggestions([
        { text: 'Product Development', type: 'trending' },
        { text: 'Customer Success Stories', type: 'trending' },
        { text: 'Engineering Best Practices', type: 'trending' },
        { text: 'Sales Techniques', type: 'trending' },
      ]);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery, isFocused, query]);

  const handleSearch = (searchQuery: string = query) => {
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    // Navigate to search results page
    router.push(`/home/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.text);
    handleSearch(suggestion.text);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        {/* Search Input */}
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-5 w-5 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search for insights, topics, or speakers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
            className={cn(
              "w-full pl-10 pr-24 h-12 text-base",
              "focus:ring-2 focus:ring-purple-500 focus:border-transparent",
              isFocused && "shadow-lg"
            )}
          />
          
          {/* Action Buttons */}
          <div className="absolute right-2 flex items-center gap-1">
            {query && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setQuery('')}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => handleSearch()}
              disabled={!query.trim() || isLoading}
              className="h-8 px-3 bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Search Suggestions Dropdown */}
        {suggestions.length > 0 && isFocused && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-background border rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="p-2">
              {!query && (
                <div className="px-3 py-2 text-sm text-muted-foreground font-medium">
                  Trending Searches
                </div>
              )}
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-3 py-2 hover:bg-muted rounded-md transition-colors flex items-center gap-2 group"
                >
                  {suggestion.type === 'trending' ? (
                    <TrendingUp className="h-4 w-4 text-muted-foreground group-hover:text-purple-600" />
                  ) : (
                    <Search className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1">{suggestion.text}</span>
                </button>
              ))}
            </div>
            
            {/* Search Tips */}
            <div className="border-t px-4 py-3 bg-muted/50">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Tip:</span> Use natural language like "how to improve customer retention" or "engineering best practices"
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Example Searches (visible when not focused and showExamples is true) */}
      {showExamples && !isFocused && !query && (
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          <span className="text-sm text-muted-foreground">Try:</span>
          {['Leadership insights', 'Product strategy', 'Team culture', 'Customer feedback'].map((example) => (
            <button
              key={example}
              onClick={() => {
                setQuery(example);
                handleSearch(example);
              }}
              className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
            >
              {example}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}